import {
  AddIcon,
  Button,
  DeleteIcon,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  showToast,
} from '@bp/ui-components';
import { Field, FieldArray, Form, Formik, FormikHelpers, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import yup from '../../../globals/yup.custom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  useCreateDivisionMutation,
  useDefaultDivisionsQuery,
  useUpdateDivisionMutation,
} from '../../../types/planung-graphql-client-defs';
import { lowercaseFirstLetter } from '../../../utils/stringHelper';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import styles from './_DivisionForms.module.scss';

type DivisionGroup = {
  name: string;
  shortName: string;
  studentCount: number | null;
  uuid: string | null;
};

type DivisionDefaultFormType = {
  groups: DivisionGroup[];
  name: string;
  uuid: string | null;
};

type DivisionDefaultFormProps = {
  divisionUuid: string | null;
  onClose: () => void;
  onLoad: (loading: boolean) => void;
};

export const DivisionDefaultForm = ({ divisionUuid, onClose, onLoad }: DivisionDefaultFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('DIVISIONS');

  const [, createDivision] = useCreateDivisionMutation();
  const [, updateDivision] = useUpdateDivisionMutation();

  const [{ data }] = useDefaultDivisionsQuery({ context });

  const division = data?.defaultDivisions.find((d) => d.uuid === divisionUuid) ?? null;

  const initialValues: DivisionDefaultFormType = {
    groups: division
      ? division.groups.map((group) => ({
          name: group.name,
          shortName: group.shortName,
          studentCount: group.studentCount ?? null,
          uuid: group.uuid,
        }))
      : [],
    name: division ? division.name : '',
    uuid: divisionUuid,
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required(t('validation.common.nameRequired')),
    groups: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required(t('validation.common.nameRequired')),
          shortName: yup.string().required(t('validation.common.shortNameRequired')),
          studentCount: yup
            .number()
            .typeError(t('validation.common.noNumber'))
            .integer(t('validation.common.integerOnly'))
            .min(0, t('validation.common.positiveNumber'))
            .notRequired(),
        }),
      )
      .min(1, t('validation.divisions.minGroups', { count: 1 }))
      .max(20, t('validation.divisions.maxGroups'))
      .required(t('validation.divisions.minGroups', { count: 1 })),
  });

  // Kürzel sind innerhalb der Vorlage eindeutig.
  const validateShortName = (value: string, shortNames: string[]) => {
    if (shortNames.includes(value)) {
      return `${value} ${lowercaseFirstLetter(t('validation.common.alreadyUsed'))}`;
    }
    return undefined;
  };

  async function handleSubmit(values: DivisionDefaultFormType, helpers: FormikHelpers<DivisionDefaultFormType>) {
    onLoad(true);

    // Vollständiger Stand: die Reihenfolge der Liste ist die Reihenfolge der Gruppen.
    const input = {
      name: values.name,
      groups: values.groups.map((group) => ({
        uuid: group.uuid,
        name: group.name,
        shortName: group.shortName,
        studentCount: group.studentCount,
      })),
    };

    const mutationResult = divisionUuid
      ? await updateDivision({ uuid: divisionUuid, input }, context)
      : await createDivision({ classUuid: null, input }, context);

    if (mutationResult.error) {
      // Eine entfernte Gruppe steckt noch in Zuordnungen oder Sollstunden: das Backend lehnt das ab.
      if (mutationResult.error.graphQLErrors.some((e) => e.extensions?.code === 'DELETE_BLOCKED')) {
        showToast(t('divisions.groupInUse'), { type: 'error' });
      } else {
        showUserDbErrorToast();
      }
      onLoad(false);
      return;
    }

    if (divisionUuid) {
      showSuccessUpdateToast();
    } else {
      showSuccessCreateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  }

  return (
    <Formik<DivisionDefaultFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, dirty, setFieldValue, setFieldTouched, errors, touched, handleChange }) => {
        const nameError = errors.name && touched.name ? errors.name : null;
        const groupError = errors.groups && !Array.isArray(errors.groups) ? errors.groups : null;

        return (
          <Form className={styles['division-default-form']}>
            <Grid useFormGap>
              <GridRow>
                <Input
                  name='name'
                  onChange={async (event) => {
                    await setFieldValue('name', event.target.value);
                    setFieldTouched('name', true);
                  }}
                  value={values.name}
                />
              </GridRow>

              <GridRow headline={t('groups.title', { count: 2 })} spacingBottom='none'>
                <FieldArray
                  name='groups'
                  render={(arrayHelpers) => {
                    return (
                      <>
                        <div className={styles.groups}>
                          {values.groups.map((group, index) => {
                            return (
                              <div key={group.uuid + '_' + index} className={styles.group}>
                                <GridRow spacingBottom='s'>
                                  <GridColumn width={11}>
                                    <Input
                                      label={`${t('groups.title', { count: 1 })} ${index + 1} - ${t('common.name')}`}
                                      name={`groups.${index}.name`}
                                      onChange={async (e) => {
                                        await setFieldValue(`groups.${index}.name`, e.target.value);
                                        setFieldTouched(`groups.${index}.name`, true);
                                      }}
                                      value={values.groups[index].name}
                                      error={
                                        getIn(touched, `groups.${index}.name`) && getIn(errors, `groups.${index}.name`)
                                          ? getIn(errors, `groups.${index}.name`)
                                          : undefined
                                      }
                                    />
                                  </GridColumn>
                                  <GridColumn width={1} className='mt-4' align='end'>
                                    <Button
                                      className='mt-1'
                                      hierarchy='ghost'
                                      icon={<DeleteIcon />}
                                      onClick={() => arrayHelpers.remove(index)}
                                    />
                                  </GridColumn>
                                </GridRow>
                                <GridRow spacingTop='s'>
                                  <GridColumn width={6}>
                                    <Field
                                      validate={(value: string) =>
                                        validateShortName(
                                          value,
                                          values.groups.filter((_, i) => i !== index).map((group) => group.shortName),
                                        )
                                      }
                                      name={`groups.${index}.shortName`}
                                      label={t('common.shortName')}
                                      error={
                                        getIn(touched, `groups.${index}.shortName`) &&
                                        getIn(errors, `groups.${index}.shortName`)
                                          ? getIn(errors, `groups.${index}.shortName`)
                                          : undefined
                                      }
                                      value={values.groups[index].shortName}
                                      onChange={handleChange}
                                      as={Input}
                                    />
                                  </GridColumn>
                                  <GridColumn width={5} offset='right'>
                                    <Input
                                      label={t('classes.studentCount.full')}
                                      name={`groups.${index}.studentCount`}
                                      onChange={async (e) => {
                                        const value = Number(e.target.value);
                                        await setFieldValue(`groups.${index}.studentCount`, value);
                                        setFieldTouched(`groups.${index}.studentCount`, true);
                                      }}
                                      value={values.groups[index].studentCount}
                                      min={0}
                                      type='number'
                                      placeholder='0'
                                      tooltipText={t('classes.studentCount.tooltip')}
                                      error={
                                        getIn(touched, `groups.${index}.studentCount`) &&
                                        getIn(errors, `groups.${index}.studentCount`)
                                          ? getIn(errors, `groups.${index}.studentCount`)
                                          : undefined
                                      }
                                    />
                                  </GridColumn>
                                </GridRow>
                              </div>
                            );
                          })}

                          <Button
                            hierarchy='tertiary'
                            icon={<AddIcon />}
                            onClick={() =>
                              arrayHelpers.push({
                                name: '',
                                shortName: '',
                                studentCount: null,
                                uuid: null,
                              })
                            }
                          >
                            {t('groups.title', { count: 1 })}
                          </Button>
                        </div>
                      </>
                    );
                  }}
                />
              </GridRow>

              <ModalBottomButtons
                additionalLeftComponent={
                  (nameError || groupError) && <div className='error-message'>{nameError ? nameError : groupError}</div>
                }
                closeButton={{ callback: () => onClose() }}
                submitButton={{ disabled: !dirty || Object.keys(errors).length > 0 }}
              />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
